import {
  constructCameraSet,
  convertToReference,
} from '@app/components/postpro-viewer/postpro-viewer.service';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CustomOverlay } from '@state/account/account.interfaces';
import { selectAllCustomOverlays } from '@state/account/account.selectors';
import { selectAccessToken } from '@state/authentication/authentication.selectors';
import { Postpro, PostproReference } from '@state/postpro/postpro.interfaces';
import * as fromPostpro from './postpro.reducer';


export const selectPostproState = createFeatureSelector<fromPostpro.State>(fromPostpro.featureKey);

export const selectAllPostpro = createSelector(
  selectPostproState,
  fromPostpro.getAll
);

export const selectPostproEntities = createSelector(
  selectPostproState,
  fromPostpro.getEntities
);

export const selectPostproIds = createSelector(
  selectPostproState,
  fromPostpro.getIds
);

export const selectPostproCount = createSelector(
  selectPostproState,
  fromPostpro.getTotal
);

export const selectPostproAvailableVariables = createSelector(
  selectPostproState,
  fromPostpro.getAvailableVariables
);

export const selectSelectedPostproVariables = createSelector(
  selectPostproState,
  fromPostpro.getSelectedVariables
);

export const selectLoadedPostproVariables = createSelector(
  selectPostproState,
  fromPostpro.getLoadedVariables
);

export const selectSelectedPostproType = createSelector(
  selectPostproState,
  fromPostpro.getSelectedType
);

export const selectReferencesLoading = createSelector(
  selectPostproState,
  fromPostpro.getReferencesLoading
);

export const selectPostproAvailableVariablesLoading = createSelector(
  selectPostproState,
  fromPostpro.getAvailableVariablesLoading
);

export const selectPostproGridOverlayName = createSelector(
  selectPostproState,
  fromPostpro.getGridOverlay
);

export const selectPostproSelectedCameras = createSelector(
  selectPostproState,
  fromPostpro.getSelectedCameras
);

export const selectAllowMultipleCameras = createSelector(
  selectPostproState,
  fromPostpro.getAllowMultipleCameras
);

export const selectPlotOverlayReferences = createSelector(
  selectPostproState,
  fromPostpro.getPlotOverlayReferences
);

export const selectPlotOverlayClipping = createSelector(
  selectPostproState,
  fromPostpro.getPlotOverlayClipping
);

export const selectPlotOverlaySide = createSelector(
  selectPostproState,
  fromPostpro.getPlotOverlaySide
);

export const selectLICOverlayReferences = createSelector(
  selectPostproState,
  fromPostpro.getLICOverlayReferences
);

/**
 * Complex Selectors
 */

export const selectPostproReferencesUnfiltered = createSelector(
  selectAllPostpro,
  selectAccessToken,
  selectPlotOverlayReferences,
  selectLICOverlayReferences,
  (unfilteredPostpro: Postpro[], token: string|null, plotOverlayReferences: PostproReference[], licReferences: PostproReference[]): PostproReference[] => {
    return unfilteredPostpro.map(postpro => convertToReference(postpro, token as string, plotOverlayReferences, licReferences));
  }
);

export const selectPostproReferencesFilteredByVariable = createSelector(
  selectPostproReferencesUnfiltered,
  selectSelectedPostproVariables,
  (unfilteredPostpro: PostproReference[], variables: string[]): PostproReference[] => {
    return unfilteredPostpro.filter(({ variable }) => variables.includes(variable));
  }
);

export const selectPostproReferences = createSelector(
  selectPostproReferencesFilteredByVariable,
  selectPostproSelectedCameras,
  (references: PostproReference[], cameras: string[]): PostproReference[] => {
    return references.filter(({ camera }) => cameras.includes(camera));
  }
);

export const selectSelectedGridOverlay = createSelector(
  selectPostproGridOverlayName,
  selectAllCustomOverlays,
  (name: string, overlays: CustomOverlay[]) => overlays.find(overlay => overlay.name === name)
);

export const selectPostproThumbnails = createSelector(
  selectPostproReferencesFilteredByVariable,
  (references: PostproReference[]) => {
    return constructCameraSet<PostproReference>(references).map(camera => ({
      camera,
      url: references.find(ref => ref.camera === camera)?.thumbnailUrl
    }));
  }
);
